import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
  useEditable,
} from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useState } from "react";
import { DialogProvider, createDialogContext } from "../Common/Dialog";
import { KYDTicketType } from "@common/interfaces/KYDTicket";
import TicketTypeWidget from "./TicketTypeWidget";
import { Wallet } from "@common/interfaces/KYDUser";
import { groupBy } from "../utils";

export const JoinWaitlistModalContext = createDialogContext();

const JoinWaitlistModal = ({
  onConfirm = () => {},
  onCancel = () => {},
  ticketTypes = [],
  isLoading,
  action,
  wallet,
}: {
  onConfirm?: (
    ticketTypesWithQuantity: {
      ticketType: KYDTicketType;
      quantity: number;
    }[]
  ) => void;
  onCancel?: () => void;
  ticketTypes: KYDTicketType[];
  isLoading: boolean;
  action: "buy" | "sell" | null;
  wallet: Wallet;
}) => {
  const { isOpen, close, title, text } = useContext(JoinWaitlistModalContext);

  const [ticketTypeCount, _setTicketTypeCount] = useState(
    ticketTypes.reduce(
      (all, tt) => ({
        ...all,
        [tt?.id]: 0,
      }),
      {}
    )
  );

  const resetCount = () => {
    _setTicketTypeCount(
      ticketTypes.reduce(
        (all, tt) => ({
          ...all,
          [tt?.id]: 0,
        }),
        {}
      )
    );
  };

  useEffect(() => {
    if (!ticketTypes || ticketTypes.length === 0) {
      close();
    }
  }, [ticketTypes]);

  const getMaximumForTicketType = (ticketType: KYDTicketType) => {
    if (!ticketType) return;

    if (action === "buy") return 10;

    const ticketsPerType = groupBy(wallet?.tickets, (t) => t.ticket_type_id);

    return ticketsPerType[ticketType.id].length;
  };

  const setTicketTypeCount = useCallback(
    (ticketTypeId: string, quantity: number) => {
      _setTicketTypeCount((count) => ({
        ...count,
        [ticketTypeId]: quantity,
      }));
    },
    [ticketTypeCount, _setTicketTypeCount]
  );

  const onCloseInternal = () => {
    // setCount(0);
    resetCount();
    close();
    onCancel();
  };

  const onConfirmInternal = async () => {
    await onConfirm(
      ticketTypes
        .map((tt) => {
          return {
            ticketType: tt,
            quantity: ticketTypeCount[tt.id],
          };
        })
        .filter((tt) => tt.quantity > 0)
    );
    close();
    resetCount();
    // setCount(0);
  };

  const buttonText = {
    buy: "JOIN WAITLIST",
    sell: `LIST ${Object.values(ticketTypeCount).reduce(
      (all: number, current: number) => all + current,
      0
    )} TICKET(S)`,
  };

  const hasCountChanged = Object.values(ticketTypeCount).some(
    (value) => value !== 0
  );

  useEffect(() => {
    if (action === null && isOpen) {
      console.warn("JoinWaitlistModal with no action");
    }
  }, [action, isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={isLoading ? () => {} : onCloseInternal}
      isCentered
      closeOnOverlayClick={true}
    >
      <ModalOverlay />
      <ModalContent
        pos={["absolute", "relative"]}
        bottom={[0, "auto"]}
        mb={[0, "auto"]}
        borderBottomLeftRadius={[0, "md"]}
        borderBottomRightRadius={[0, "md"]}
      >
        <ModalCloseButton />
        <ModalHeader paddingTop={4}>
          <VStack gap={0} width={"full"}>
            <Text w="full">{title}</Text>
          </VStack>
        </ModalHeader>
        <ModalBody textAlign={"left"}>
          <VStack spacing={2}>
            <Text w="full" color="gray.600" fontSize={14}>
              {text}
            </Text>
            {ticketTypes.map((ticketType) => (
              <TicketTypeWidget
                ticketType={ticketType}
                onChangeCount={(quantity) =>
                  setTicketTypeCount(ticketType.id, quantity)
                }
                disabled={isLoading}
                maximum={getMaximumForTicketType(ticketType)}
                hidePrice={action === "sell"}
              />
            ))}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={onConfirmInternal}
            isDisabled={!hasCountChanged || isLoading}
            disabled={!hasCountChanged || isLoading}
            isLoading={isLoading}
            sx={{
              width: "full",
              backgroundColor: "lightGreen",
              borderRadius: "xl",
              borderWidth: 2,
              borderColor: "darkGreen",
              color: "black",
              p: 6,
              fontSize: 18,
              fontWeight: "bold",
              _hover: {
                backgroundColor: "darkGreen",
              },
            }}
          >
            {buttonText[action]}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const withWaitlistModal =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={JoinWaitlistModalContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default JoinWaitlistModal;
